/**
 * Voice session transport.
 *
 * Owns the lifecycle of one ElevenLabs Conversational AI websocket
 * call:
 *
 *  1. Ask the backend for a short-lived signed URL.
 *  2. Request microphone permission from the browser.
 *  3. Open the websocket via the `@elevenlabs/client` SDK.
 *  4. Forward state transitions to the modal as callbacks.
 *  5. End the call cleanly when the operator clicks "Beeindigen"
 *     OR the websocket drops on its own.
 *
 * This module does NOT render anything; the modal owns the visual
 * state. Splitting transport from rendering keeps the modal free of
 * websocket lifecycle bugs and keeps this module trivially testable
 * with a fake `Conversation` constructor.
 *
 * Bundle strategy: the `@elevenlabs/client` SDK is loaded from a
 * public CDN (jsDelivr) at runtime the first time the operator
 * opens the modal. We deliberately do NOT bundle the SDK into the
 * widget IIFE: it weighs 100+ kB gzipped and would blow the
 * <50 kB target the chat-only path commits to (CLAUDE.md sectie 4).
 * The SDK loads at the moment the operator clicks the mic button,
 * which is the only path that needs it. The package is still
 * declared in `package.json` so TypeScript can resolve types in
 * `package.json#peerDependencies`-equivalent fashion via the
 * runtime CDN URL pinned below.
 */

import { type VoiceSession, fetchVoiceSession } from "../api";
import type { WidgetBootConfig } from "../types";

/** Discriminated union of voice-session lifecycle states. */
export type VoiceState =
  | { kind: "idle" }
  | { kind: "requesting_permission" }
  | { kind: "connecting" }
  | { kind: "live" }
  | { kind: "ended" }
  | { kind: "error"; reason: VoiceError };

/** Why the voice session failed. */
export type VoiceError = "permission_denied" | "session_unavailable" | "connection_failed";

/** Callback shape the modal hands to the transport. */
export interface VoiceCallbacks {
  onState: (state: VoiceState) => void;
}

/**
 * Minimal interface of the `@elevenlabs/client` SDK we rely on.
 *
 * We model it ourselves (rather than depending on the SDK's
 * exported types) so the rest of the widget compiles even when the
 * SDK is not installed yet. The integration pass installs it via
 * `pnpm`; until then the dynamic import path is the only reference.
 */
interface ConversationLike {
  endSession(): Promise<void>;
}

interface ElevenLabsClientSDK {
  Conversation: {
    startSession(options: {
      signedUrl: string;
    }): Promise<ConversationLike>;
  };
}

/**
 * One running voice call. The handle is opaque to the modal; it
 * exposes only `end()` so the modal cannot reach into the SDK by
 * accident.
 */
export interface VoiceSessionHandle {
  end(): Promise<void>;
}

/**
 * Open a voice session.
 *
 * Walks the state machine `idle -> requesting_permission ->
 * connecting -> live` and calls `onState` on every transition. On
 * any failure the state machine settles on `error` with a
 * structured `reason` the modal maps to localized microcopy.
 *
 * The returned handle's `end()` is idempotent: calling it more
 * than once is a no-op. The modal calls it from both the "end"
 * button and the close-X button, so accidental double-clicks must
 * not throw.
 */
export async function startVoiceSession(
  boot: WidgetBootConfig,
  callbacks: VoiceCallbacks,
): Promise<VoiceSessionHandle> {
  callbacks.onState({ kind: "requesting_permission" });

  // Browser mic permission request. We do NOT keep the resulting
  // MediaStream; the SDK opens its own when the websocket connects.
  // Asking ourselves first surfaces a clear `permission_denied`
  // path so the modal can render a calm Dutch copy instead of the
  // browser's generic prompt being denied silently.
  let permissionStream: MediaStream | null = null;
  try {
    permissionStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
  } catch {
    callbacks.onState({ kind: "error", reason: "permission_denied" });
    return _noopHandle();
  }
  // Release the probe stream right away; the SDK opens its own.
  for (const track of permissionStream.getTracks()) {
    track.stop();
  }

  callbacks.onState({ kind: "connecting" });

  let session: VoiceSession;
  try {
    session = await fetchVoiceSession(boot);
  } catch {
    callbacks.onState({ kind: "error", reason: "session_unavailable" });
    return _noopHandle();
  }

  let conversation: ConversationLike | null = null;
  try {
    const sdk = (await _loadElevenLabsSDK()) as ElevenLabsClientSDK;
    conversation = await sdk.Conversation.startSession({
      signedUrl: session.signed_url,
    });
  } catch {
    callbacks.onState({ kind: "error", reason: "connection_failed" });
    return _noopHandle();
  }

  callbacks.onState({ kind: "live" });

  let ended = false;
  return {
    async end(): Promise<void> {
      if (ended) return;
      ended = true;
      try {
        await conversation?.endSession();
      } catch {
        // The SDK's endSession occasionally throws when the
        // websocket has already gone away (e.g. ElevenLabs closed
        // it server-side). Swallowing keeps the modal's close
        // path symmetric with the success path.
      }
      callbacks.onState({ kind: "ended" });
    },
  };
}

/**
 * Pin the CDN URL the widget loads at runtime. We pick a specific
 * version so a future SDK release cannot silently change the wire
 * shape under us. Bumping is a one-line PR + a re-deploy of the
 * widget bundle. jsDelivr serves npm packages over a CDN with
 * aggressive caching, which gives every customer site fast first-
 * paint of the modal even on cold load.
 */
const _ELEVENLABS_SDK_URL = "https://cdn.jsdelivr.net/npm/@elevenlabs/client@1.7.0/+esm";

/**
 * Lazy-load `@elevenlabs/client` from a public CDN so the chat-
 * only code path never pays the bundle cost. The dynamic import
 * targets a pinned URL string (NOT the bare specifier) so Vite's
 * bundler does not try to inline the SDK into our IIFE during
 * production builds.
 *
 * A runtime miss (CDN down, customer behind a strict CSP) surfaces
 * as `connection_failed` in the modal microcopy rather than a build
 * break.
 *
 * Memoized via `_sdkPromise`: the first caller (either `preloadVoiceSdk`
 * on widget mount or the lazy path on mic click) kicks off the import,
 * later callers reuse the same promise. That makes preload + lazy-load
 * a single fetch instead of two.
 */
let _sdkPromise: Promise<unknown> | null = null;

async function _loadElevenLabsSDK(): Promise<unknown> {
  if (_sdkPromise === null) {
    // The /* @vite-ignore */ comment tells Vite to leave the import
    // expression untouched in the output, so the bundler does not try
    // to resolve the dynamic specifier at build time. At runtime the
    // browser's native ESM loader fetches the CDN URL.
    _sdkPromise = import(/* @vite-ignore */ _ELEVENLABS_SDK_URL);
  }
  return await _sdkPromise;
}

/**
 * Warm the browser cache for the ElevenLabs SDK without starting a
 * call. Intended to fire once on widget mount when `voice_enabled` is
 * true, so the first mic click resolves the dynamic import instantly
 * instead of paying the CDN round-trip + ESM resolve at the moment
 * the operator wants to talk.
 *
 * Idempotent: calls after the first reuse the same in-flight (or
 * resolved) promise via `_sdkPromise`. Best-effort: callers do NOT
 * await this and a rejection is swallowed so a slow or blocked CDN
 * does not surface as an error before the user has even tried voice.
 * The lazy path on mic click runs the same import again and surfaces
 * `connection_failed` if the CDN is genuinely unreachable.
 *
 * Critically this does NOT request mic permission, open a websocket,
 * or hit the backend for a signed URL. It only triggers the JS
 * module fetch so the browser caches it.
 */
export function preloadVoiceSdk(): void {
  void _loadElevenLabsSDK().catch(() => {
    // Reset so a later real start() retries cleanly instead of
    // inheriting the rejected promise. The rejection itself is
    // intentionally swallowed: preload is best-effort.
    _sdkPromise = null;
  });
}

/**
 * Return a no-op handle for the failure paths so the modal can
 * always call `.end()` without a null check. The callbacks already
 * fired the terminal state.
 */
function _noopHandle(): VoiceSessionHandle {
  return {
    async end(): Promise<void> {
      /* terminal state already reported */
    },
  };
}
